import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Spinner, Form } from 'react-bootstrap';
import { Bot, Send, X, RotateCcw, Minus } from 'lucide-react';
import axios from 'axios';

import "../ComponentsCss/SusanChatbot.css";
import ReactMarkdown from 'react-markdown';

const SusanChatbot = ({ show, onHide, userId }) => {
  // Load chat history from localStorage
  const loadChatHistory = () => {
    try {
      const savedMessages = localStorage.getItem(`susan-chat-${userId}`);
      if (savedMessages) {
        return JSON.parse(savedMessages).map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
    return [
      {
        id: 1,
        type: 'bot',
        text: 'Hello! I\'m SUSAN, your AI assistant. How can I help you today?',
        timestamp: new Date()
      }
    ];
  };
const [position, setPosition] = useState({ x: 20, y: 20 });
const [isDragging, setIsDragging] = useState(false);
const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
const widgetRef = useRef(null); 


  const [messages, setMessages] = useState(loadChatHistory());
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Save chat history to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(`susan-chat-${userId}`, JSON.stringify(messages));
      } catch (error) {
        console.error('Error saving chat history:', error);
      }
    }
  }, [messages, userId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (show && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [show, isMinimized]);
  // Drag functionality
const handleMouseDown = (e) => {
  if (e.target.closest('.susan-ai-minimized-close')) return;
  setIsDragging(true);
  const rect = widgetRef.current.getBoundingClientRect();
  setDragOffset({
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  });
};

useEffect(() => {
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const widgetWidth = widgetRef.current?.offsetWidth || 280;
    const widgetHeight = widgetRef.current?.offsetHeight || 60;

    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;
    newX = Math.max(0, Math.min(newX, viewportWidth - widgetWidth));
    newY = Math.max(0, Math.min(newY, viewportHeight - widgetHeight));
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => setIsDragging(false);

  if (isDragging) {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  return () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
}, [isDragging, dragOffset]);


  const formatBotResponse = (text) => {
    if (!text) return null;

    let normalizedText = text
      .replace(/Steps (\d+)\./g, '\n\nStep $1.')
      .replace(/Step(\d+)\./g, '\n\nStep $1.')
      .replace(/\s+-\s+/g, '\n- ')
      .trim();

    const sections = normalizedText.split(/\n\n+/);
    
    
    return (
      <div className="susan-ai-formatted-response">
        {sections.map((section, index) => {
          const trimmedSection = section.trim();
          if (!trimmedSection) return null;

          const stepMatch = trimmedSection.match(/^Step\s+(\d+)[.:]\s*(.*)/is);
          if (stepMatch) {
            const stepNumber = stepMatch[1];
            const stepContent = stepMatch[2];
            
            return (
              <div key={index} className="susan-ai-step-container">
                <div className="susan-ai-step-header">
                  <span className="susan-ai-step-number">Step {stepNumber}</span>
                </div>
                <div className="susan-ai-step-content">
                 <div className="susan-ai-markdown-content">
<ReactMarkdown>
    {stepContent}
</ReactMarkdown>
</div>
                </div>
              </div>
            );
          }

          return (
            <div key={index} className="susan-ai-paragraph">
              <div className="susan-ai-markdown-content">
<ReactMarkdown>
    {trimmedSection}
</ReactMarkdown>
</div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await axios.post('https://u1wdtz7pr6.execute-api.ap-southeast-2.amazonaws.com/prod/api/ai/kb-ask', {
        query: userMessage.text
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: response.data.answer || 'I apologize, but I couldn\'t process your request.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: 'Sorry, I encountered an error. Please try again later.',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleReset = () => {
    const initialMessage = {
      id: 1,
      type: 'bot',
      text: 'Hello! I\'m SUSAN, your AI assistant. How can I help you today?',
      timestamp: new Date()
    };
    setMessages([initialMessage]);
    setInputMessage('');
    // Clear localStorage
    localStorage.removeItem(`susan-chat-${userId}`);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleRestore = (e) => {
  if (!isDragging) {
    setIsMinimized(false);
  }
};


  const handleClose = () => {
    setIsMinimized(false);
    onHide();
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <>
      {/* Full Modal */}
      <Modal
        show={show && !isMinimized}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="susan-ai-chatbot-modal"
        contentClassName="susan-ai-chatbot-content"
        keyboard={true}
        enforceFocus={true}
        restoreFocus={true}
        autoFocus={true}
      >
        <Modal.Header className="susan-ai-header-wrapper">
          <div className="susan-ai-header-left-section">
            <div className="susan-ai-bot-avatar-circle">
              <Bot size={24} />
            </div>
            <div className="susan-ai-header-text-block">
              <h5 className="susan-ai-title-text">SUSAN AI Assistant</h5>
              <small className="susan-ai-subtitle-text">Knowledge Base Powered</small>
            </div>
          </div>
          <div className="susan-ai-header-actions">
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleReset}
              className="susan-ai-reset-btn"
              title="Reset conversation"
            >
              <RotateCcw size={16} />
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleMinimize}
              className="susan-ai-minimize-btn"
              title="Minimize"
            >
              <Minus size={16} />
            </Button>
            <Button
              variant="link"
              className="susan-ai-close-btn"
              onClick={handleClose}
            >
              <X size={24} />
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body className="susan-ai-body-container p-0">
          <div className="susan-ai-messages-scroll-area">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`susan-ai-msg-wrapper ${message.type === 'user' ? 'susan-ai-user-msg' : 'susan-ai-bot-msg'}`}
              >
                <div className="susan-ai-msg-content-flex">
                  {message.type === 'bot' && (
                    <div className="susan-ai-avatar susan-ai-bot-avatar-small">
                      <Bot size={18} />
                    </div>
                  )}
                  <div className={`susan-ai-bubble ${message.isError ? 'susan-ai-error-bubble' : ''}`}>
                    {message.type === 'bot' ? (
                      <>
                        {formatBotResponse(message.text)}
                        <span className="susan-ai-timestamp">{formatTime(message.timestamp)}</span>
                      </>
                    ) : (
                      <>
                        <p className="mb-1">{message.text}</p>
                        <span className="susan-ai-timestamp">{formatTime(message.timestamp)}</span>
                      </>
                    )}
                  </div>
                  {message.type === 'user' && (
                    <div className="susan-ai-avatar susan-ai-user-avatar-small">
                      {userId?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="susan-ai-msg-wrapper susan-ai-bot-msg">
                <div className="susan-ai-msg-content-flex">
                  <div className="susan-ai-avatar susan-ai-bot-avatar-small">
                    <Bot size={18} />
                  </div>
                  <div className="susan-ai-bubble susan-ai-typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </Modal.Body>

        <Modal.Footer className="susan-ai-footer-wrapper">
          <Form.Group className="w-100 mb-0">
            <div className="susan-ai-input-flex">
              <Form.Control
                ref={inputRef}
                as="textarea"
                rows={1}
                placeholder="Ask SUSAN anything..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping}
                className="susan-ai-text-input"
              />
              <Button
                variant="primary"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isTyping}
                className="susan-ai-send-btn"
              >
                {isTyping ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <Send size={18} />
                )}
              </Button>
            </div>
          </Form.Group>
        </Modal.Footer>
      </Modal>

      {/* Minimized Widget */}
      {show && isMinimized && (
 <div
    ref={widgetRef}
    className={`susan-ai-minimized-widget ${isDragging ? 'dragging' : ''}`}
    style={{
      left: `${position.x}px`,
      top: `${position.y}px`,
      right: 'auto',
      bottom: 'auto'
    }}
    onMouseDown={handleMouseDown}
    onClick={handleRestore}
  >    <div className="susan-ai-minimized-content">
      <div className="susan-ai-minimized-avatar">
        <Bot size={20} />
      </div>
      <div className="susan-ai-minimized-text">
        <span className="susan-ai-minimized-title">SUSAN AI</span>
        <small className="susan-ai-minimized-subtitle">Click to restore</small>
      </div>
      {/* {messages.length > 1 && (
        <span className="susan-ai-message-badge">{messages.length - 1}</span>
      )} */}
    </div>
    <Button
      variant="link"
      className="susan-ai-minimized-close"
      onClick={(e) => {
        e.stopPropagation();
        handleClose();
      }}
    >
      <X size={16} />
    </Button>
  </div>
)}

    </>
  );
};

export default SusanChatbot;